import { apiRequest } from "./queryClient";

/**
 * Convert a base64-url VAPID public key into the Uint8Array the
 * PushManager expects.
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export function isPushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function getNotificationPermission(): NotificationPermission | "unsupported" {
  if (!isPushSupported()) return "unsupported";
  return Notification.permission;
}

async function getRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.ready;
    return reg;
  } catch {
    return null;
  }
}

export async function getCurrentSubscription(): Promise<PushSubscription | null> {
  const reg = await getRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

/**
 * Request browser permission, subscribe via PushManager, and POST the
 * subscription to the server. Returns the active subscription on success.
 */
export async function subscribeToPush(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null;

  // 1. Permission
  let permission = Notification.permission;
  if (permission === "default") {
    permission = await Notification.requestPermission();
  }
  if (permission !== "granted") return null;

  // 2. Fetch the server's VAPID public key
  let vapidKey: string;
  try {
    const res = await fetch("/api/push/vapid-public-key", { credentials: "include" });
    if (!res.ok) return null;
    const data = await res.json();
    vapidKey = data.publicKey;
    if (!vapidKey) return null;
  } catch {
    return null;
  }

  // 3. Subscribe via the SW's PushManager
  const reg = await getRegistration();
  if (!reg) return null;

  let sub: PushSubscription;
  try {
    sub =
      (await reg.pushManager.getSubscription()) ??
      (await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      }));
  } catch (err) {
    console.error("[push] Failed to subscribe:", err);
    return null;
  }

  // 4. Send to backend
  const json = sub.toJSON();
  const keys = (json.keys ?? {}) as { p256dh?: string; auth?: string };
  if (!keys.p256dh || !keys.auth) return null;

  try {
    await apiRequest("POST", "/api/push-subscriptions", {
      endpoint: sub.endpoint,
      p256dh: keys.p256dh,
      auth: keys.auth,
      userAgent: navigator.userAgent,
    });
  } catch (err) {
    console.error("[push] Failed to register subscription with server:", err);
    return null;
  }

  return sub;
}

/**
 * Task #325 — silently re-bind this device's existing push subscription to
 * the current login session. Never prompts: does nothing unless notification
 * permission is already granted AND a browser subscription already exists.
 * The server ties pushes to the live session, so this re-registration is
 * what turns pushes back on after a re-login (logout silences them
 * server-side without unsubscribing the device).
 */
export async function rebindPushSubscription(): Promise<boolean> {
  if (!isPushSupported()) return false;
  if (Notification.permission !== "granted") return false;

  const sub = await getCurrentSubscription();
  if (!sub) return false;

  const json = sub.toJSON();
  const keys = (json.keys ?? {}) as { p256dh?: string; auth?: string };
  if (!keys.p256dh || !keys.auth) return false;

  try {
    await apiRequest("POST", "/api/push-subscriptions", {
      endpoint: sub.endpoint,
      p256dh: keys.p256dh,
      auth: keys.auth,
      userAgent: navigator.userAgent,
    });
    return true;
  } catch {
    return false;
  }
}

/**
 * Unsubscribe from push on this device and tell the server to forget it.
 */
export async function unsubscribeFromPush(): Promise<boolean> {
  const sub = await getCurrentSubscription();
  if (!sub) return true;

  try {
    await apiRequest("DELETE", "/api/push-subscriptions", { endpoint: sub.endpoint });
  } catch {
    /* ignore */
  }

  try {
    await sub.unsubscribe();
    return true;
  } catch {
    return false;
  }
}
